const DEFAULT_MAX_AGE = 12 * 60 * 60 * 1000;
const DEFAULT_TIMEOUT = 450;
const MAX_VALUE_LENGTH = 4_500_000;

function parseSnapshot(value) {
  if (!value || typeof value !== 'string') return null;
  try {
    const snapshot = JSON.parse(value);
    return snapshot && typeof snapshot === 'object' ? snapshot : null;
  } catch {
    return null;
  }
}

function withTimeout(register, timeoutMs) {
  return new Promise(resolve => {
    let settled = false;
    const finish = value => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    try { register(finish); } catch { finish(null); }
  });
}

export function createTelegramDeviceCache({
  webApp,
  key = 'reverse_snapshot_v1',
  now = () => Date.now(),
  maxAge = DEFAULT_MAX_AGE,
  timeoutMs = DEFAULT_TIMEOUT,
} = {}) {
  const storage = webApp?.DeviceStorage;
  const versionSupported = typeof webApp?.isVersionAtLeast !== 'function'
    || webApp.isVersionAtLeast('9.0');
  const available = Boolean(storage && versionSupported);
  let lastDataValue = '';
  let writeChain = Promise.resolve(false);

  async function get(userId) {
    if (!available || !userId || typeof storage.getItem !== 'function') return null;
    const value = await withTimeout(done => {
      storage.getItem(key, (error, storedValue) => done(error ? null : storedValue));
    }, timeoutMs);
    const snapshot = parseSnapshot(value);
    if (!snapshot || String(snapshot.userId || '') !== String(userId)) return null;
    if (!Number.isFinite(snapshot.savedAt) || now() - snapshot.savedAt > maxAge) return null;
    try { lastDataValue = JSON.stringify(snapshot.data || {}); } catch { lastDataValue = ''; }
    return snapshot.data && typeof snapshot.data === 'object' ? snapshot.data : null;
  }

  async function set(userId, data) {
    if (!available || !userId || !data || typeof storage.setItem !== 'function') return false;
    let value;
    let dataValue;
    try {
      dataValue = JSON.stringify(data);
      value = JSON.stringify({ userId: String(userId), savedAt: now(), data });
    }
    catch { return false; }
    if (value.length > MAX_VALUE_LENGTH) return false;
    if (dataValue === lastDataValue) return true;
    writeChain = writeChain.catch(() => false).then(async () => {
      const result = await withTimeout(done => {
        storage.setItem(key, value, (error, success) => done(error ? false : success !== false));
      }, timeoutMs);
      if (result === true) lastDataValue = dataValue;
      return result === true;
    });
    return writeChain;
  }

  return { available, get, set, flush: () => writeChain.catch(() => false) };
}
